import invariant from 'tiny-invariant'
import { v4 as uuidv4 } from 'uuid'

import type { definitions } from '@luxuryescapes/contract-svc-accommodation'

import { getOfferInclusionDescriptionFromMarkdown } from 'api/mappers/offerInclusionMap'
import config from 'constants/config'
import { OFFER_TYPE_ALWAYS_ON, OFFER_TYPE_BED_BANK } from 'constants/offer'
import { EmptyArray, sortBy } from 'lib/array/arrayUtils'
import type { ChannelMarkupDetails } from 'lib/channelMarkup/channelMarkup'
import { applyChannelMarkupAndRoundUpToTen } from 'lib/channelMarkup/channelMarkup'
import type { ChannelMarkupSource } from 'lib/channelMarkup/channelMarkupV2'
import { resolveChannelMarkupDetails, resolveMemberChannelMarkupDetails } from 'lib/channelMarkup/channelMarkupV2'
import { mapBedbankBoardCode } from 'lib/hotels/bedbankUtils'
import { mapObject } from 'lib/object/objectUtils'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { assertUnreachable } from 'lib/staticAssert/staticAssertUtils'

import { mapPromotionType } from './propertySharedMapper'

const NO_CHANNEL_MARKUP: ChannelMarkupDetails = { markup: 1, isApplied: false }

type ServerAvailabilityResponseProperty = definitions['AvailabilityResponseProperty']

type ServerRateListItem = definitions['RateLists']['hotelOnly'][number]

type ServerUnifiedRate = definitions['UnifiedRate']

type ServerMetaKey = ServerUnifiedRate['meta']
type ServerLPCMetaKey = definitions['LPCMetaKey']
type ServerLPPMetaKey = definitions['LPPMetaKey']
type ServerRateType = ServerMetaKey['type']

type ServerBundle =
  | NonNullable<ServerLPCMetaKey['bundle']>
  | NonNullable<ServerLPPMetaKey['bundle']>
type ServerCancellationPolicy = definitions['CancellationPolicies']
type ServerInclusion = definitions['Inclusion']
type ServerOptionalBundle = definitions['OptionalBundle']
type ServerPaymentSchedule =
  | NonNullable<ServerLPCMetaKey['paymentSchedule']>
  | NonNullable<ServerLPPMetaKey['paymentSchedule']>
type ServerPromotion = definitions['Promotion']
type ServerRateTotals = definitions['RateTotals']
type ServerRefundableStatus = definitions['RefundableStatus']

function isLpcMeta(meta: ServerMetaKey): meta is ServerLPCMetaKey {
  return meta.type === 'reservation'
}

function isBedbankMeta(meta: ServerMetaKey): meta is ServerLPPMetaKey {
  return meta.type === 'bedbank'
}

function mapInclusionDescription(
  serverRateType: ServerRateType,
  text: string,
): string {
  switch (serverRateType) {
    case 'reservation':
      return getOfferInclusionDescriptionFromMarkdown(text)
    case 'bedbank':
      return text
    default:
      assertUnreachable(serverRateType)
  }
}

function mapBundle({
  bundleId,
  // status, // This is unused.
  tickets,
  hotelPrice: {
    sellingPrice: hotelSellingPrice,
    luxplusPrice: hotelLuxPlusSellingPrice,
  },
}: ServerBundle): App.Property.Bundle | undefined {
  if (!bundleId || !tickets.length) return undefined
  return {
    bundleId,
    tickets: tickets.map(t => ({
      experienceId: t.experienceId,
      productId: t.productId,
      price: t.ticketPrice,
    })),
    hotelSellingPrice,
    hotelLuxPlusSellingPrice,
  }
}

export function mapInclusion(
  serverRateType: ServerRateType,
  { text, icon }: ServerInclusion,
  inclusionType: 'normal' | 'bonus' | 'luxPlus',
): App.OfferInclusion {
  const description = mapInclusionDescription(serverRateType, text)
  return {
    id: uuidv4(),
    description,
    symbol: determineOfferFeatureSymbol(icon || description),
    isBonus: inclusionType === 'bonus',
    isLuxPlusBonus: inclusionType === 'luxPlus',
  }
}

function mapOptionalBundle({
  bundleId,
  // status, // This is unused.
  totalPrice,
  tickets,
}: ServerOptionalBundle): App.Property.OptionalBundle {
  return {
    bundleId,
    totalPrice,
    tickets: tickets.map(({ experienceId, productId, ticketPrice, inclusion }) => ({
      experienceId,
      productId,
      price: ticketPrice,
      // NB: We hard-code `serverRateType` to `'bedbank'` to avoid further processing via
      // `getOfferInclusionDescriptionFromMarkdown`.
      inclusion: mapInclusion('bedbank', inclusion, 'normal'),
    })),
  }
}

function mapPaymentMethod({
  paymentMethod,
  payAtProperty,
}: Pick<ServerLPPMetaKey, 'paymentMethod' | 'payAtProperty'>, currency: string): App.Property.PaymentMethod {
  // TODO: Make this field non-optional in accommodation service.
  switch (paymentMethod ?? 'leCollect') {
    case 'leCollect':
      return { type: 'leCollect' }
    case 'propertyCollect':
      return {
        type: 'propertyCollect',
        totals: payAtProperty && payAtProperty.currencyCode !== currency ? {
          displayPrice: payAtProperty.displayPrice,
          currency: payAtProperty.currencyCode,
        } : undefined,
      }
  }
}

function mapPaymentScheduleInfo({
  templateId,
  supplierDueDays,
}: ServerPaymentSchedule): App.PaymentScheduleInfo {
  return {
    staticSchedule: {
      templateId,
      supplierPaymentNumberOfDays: supplierDueDays,
    },
  }
}

export function mapPromotion({
  type,
  name,
  travelPeriod: {
    from: travelFromIsoDate,
    to: travelToIsoDate,
  },
  minimumLos,
  sourceId,
  // marketedLos, // This is unused.
  // defaultOccupancy, // This is unused.
}: ServerPromotion): App.Property.RatePromotion {
  const promotionType = mapPromotionType(type)
  return {
    ...promotionType,
    campaignId: promotionType.isCampaign ? sourceId : undefined,
    name,
    travelFromIsoDate,
    travelToIsoDate,
    minimumLos,
  }
}

function mapCancellationPolicy({
  amount,
  description,
  start,
  end,
  nonRefundable,
}: ServerCancellationPolicy): App.Property.CancellationPolicy {
  return {
    startIsoDateTime: start,
    endIsoDateTime: end,
    penaltyPrice: amount,
    isFullyNonRefundable: nonRefundable,
    originalDescription: description,
  }
}

/**
 * @NOTE For MVP upgrades are excluded where source or target are a bundle rate
 */
function mapTargetPackageUpgradeRelation(serverRate: ServerUnifiedRate, ratesMap: Record<string, ServerUnifiedRate>): string | undefined {
  if (serverRate.meta.bundle) return
  // We can technically have multiple package-upgrade targets, but in practice there's only one at a time.
  const targetRateId = serverRate.relations?.packageUpgrade?.find(({ rateId, value }) => value === 'package_upgrade' && rateId !== serverRate.id)?.rateId
  return targetRateId !== undefined && ratesMap[targetRateId]?.meta.bundle ? undefined : targetRateId
}

function mapHeadlineCancellationPolicy(
  serverRefundableStatus: ServerRefundableStatus,
  serverFullyRefundableUntil: string | undefined,
  serverPartiallyRefundableUntil: string | undefined,
): ['fullyRefundable' | 'partiallyRefundable', string] | ['nonRefundable', undefined] {
  switch (serverRefundableStatus) {
    case 'refundable':
      return ['fullyRefundable', serverFullyRefundableUntil!]
    case 'partially':
      return ['partiallyRefundable', serverPartiallyRefundableUntil!]
    case 'non-refundable':
      return ['nonRefundable', undefined]
    default:
      return assertUnreachable(serverRefundableStatus)
  }
}

export function mapRateTotals(
  serverRateTotals: ServerRateTotals,
  markupDetails: ChannelMarkupDetails = NO_CHANNEL_MARKUP,
): App.PropertyRateTotals {
  // Channel markup (LPP/LPC): uplift displayPrice (rounded to ten); the surcharge is absorbed
  // by the ex-tax sell amounts. Taxes, fees and RRP (valuedUpToPrice) are untouched.
  // Property fees are a cost pass-through excluded from the markup base and its round-to-ten
  // (matching svc-order, which marks up the total without property fees), then re-added.
  const markedPriceExcludingPropertyFees = applyChannelMarkupAndRoundUpToTen(
    serverRateTotals.displayPrice - serverRateTotals.propertyFees,
    markupDetails,
  )
  const displayPrice = markedPriceExcludingPropertyFees + serverRateTotals.propertyFees
  const surcharge = displayPrice - serverRateTotals.displayPrice
  return {
    ...serverRateTotals,
    displayPrice,
    sellingPrice: serverRateTotals.sellingPrice + surcharge,
    beforeTaxesFeesPrice: serverRateTotals.beforeTaxesFeesPrice + surcharge,
    // TODO: Incorporate into accommodation service
    societeHotelPrice: markedPriceExcludingPropertyFees,
    // TODO(HP-1274): Use server's `discount` field once it's trustworthy.
    discount: getValueOffPercent(serverRateTotals.valuedUpToPrice, displayPrice),
  }
}

function mapPropertyRate(serverRate: ServerUnifiedRate, currency: string, ratesMap: Record<string, ServerUnifiedRate>, markupSource?: ChannelMarkupSource): App.RawPropertyRate {
  const serverRateMeta = serverRate.meta
  // Channel markup: bedbank (LPP) rates are keyed on bedbankPropertyId (so a blacklist entry
  // must list it alongside the LE offer id); LPC (reservation) rates on the LE offerId.
  const [markupId, markupOfferType] = isBedbankMeta(serverRateMeta)
    ? [serverRateMeta.bedbankPropertyId, OFFER_TYPE_BED_BANK] as const
    : isLpcMeta(serverRateMeta)
      ? [serverRateMeta.offerId, OFFER_TYPE_ALWAYS_ON] as const
      : [undefined, undefined] as const
  const markupPricing = { total: serverRate.total?.displayPrice, luxPlusPrice: serverRate.luxPlus?.displayPrice }
  const markupDetails = markupId
    ? resolveChannelMarkupDetails(markupId, markupOfferType, markupSource, markupPricing)
    : NO_CHANNEL_MARKUP
  const memberMarkupDetails = markupId
    ? resolveMemberChannelMarkupDetails(markupId, markupOfferType, markupSource, markupPricing)
    : NO_CHANNEL_MARKUP
  // NB: These fields were last audited against v21.12.0 of `contract-svc-accommodation`.
  const [headlineCancellationPolicyType, headlineCancellationPolicyStart] = mapHeadlineCancellationPolicy(serverRate.refundableStatus, serverRate.fullRefundableUntil, serverRate.partiallyRefundableUntil)
  const baseRelations: Omit<App.RawPropertyRate['relations'], 'paymentMethod'> = {
    upgradeTargets: {
      room: undefined, // Room upgrades are currently not supported, but we might want to add this in the future.
      package: mapTargetPackageUpgradeRelation(serverRate, ratesMap),
    },
  }
  const baseRate: Omit<App.RawPropertyRate, 'service' | 'exceedsIncludedGuests' | 'hasAppliedDiscount' | 'isMobilePromotion' | 'paymentScheduleInfo' | 'paymentMethod' | 'relations'> = {
    /* Identifiers */
    propertyId: serverRate.propertyId,
    roomId: serverRate.roomId,
    rateId: serverRate.id,
    optionId: serverRate.optionId,
    /* Deduplication keys */
    groupKey: serverRate.groupKey,
    bundlingKey: serverRate.bundlingKey,
    isFlightBundle: !!serverRateMeta.isFlightBundle,
    boardCode: mapBedbankBoardCode(serverRate.boardCode),
    /* Details */
    currency,
    /* Configuration options */
    optionalBundles: serverRate.optionalBundles?.map(mapOptionalBundle) ?? EmptyArray,
    /* Content */
    name: serverRate.name,
    staffLabel: serverRate.staffLabel,
    /* Inclusions */
    promotion: serverRate.promotion ? mapPromotion(serverRate.promotion) : undefined,
    inclusions: serverRate.inclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'normal')),
    bonusInclusions: serverRate.bonusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'bonus')),
    luxPlusInclusions: serverRate.luxPlusInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'luxPlus')),
    bundle: serverRateMeta.bundle ? mapBundle(serverRateMeta.bundle) : undefined,
    bundleInclusions: serverRate.bundleInclusions.map(serverInclusion => mapInclusion(serverRate.meta.type, serverInclusion, 'normal')),
    /* Cancellation policies */
    cancellationPolicies: serverRate.cancellationPolicies.map(mapCancellationPolicy),
    /* Pricing */
    totals: mapRateTotals(serverRate.total, markupDetails),
    // TODO(Xiao): Guard behind relevant feature flags:
    //   - `config.LUXURY_PLUS_ENABLED` (see `getIsLuxPlusEnabled`); and
    //   - `OptimizelyFeatureFlags.luxPlusLppPricingEnabled`.
    luxPlusTotals: serverRate.luxPlus && mapRateTotals(serverRate.luxPlus, memberMarkupDetails),
    /* Metadata */
    availableRooms: serverRate.availableRooms,
    mergedRates: serverRateMeta.mergedRates ?? EmptyArray,
    /* (Irrelevant) */
    // `serverRate.occupancies` is not set.
    // `serverRate.margin` is filtered out for non-admins.
    // `serverRate.groupScore` is filtered out for non-admins.
    /* TODO: Audit, document, tighten contract */
    fullyRefundableUntil: serverRate.fullRefundableUntil,
    headlineCancellationPolicyType,
    headlineCancellationPolicyStart,
  }
  if (isLpcMeta(serverRateMeta)) {
    return {
      ...baseRate,
      /* Details */
      service: {
        /* Identifiers */
        type: 'lpc',
        offerId: serverRateMeta.offerId,
        propertyId: serverRateMeta.reservationPropertyId,
        packageId: serverRateMeta.packageId ?? serverRateMeta.lePackageId,
        roomId: serverRateMeta.reservationRoomTypeId,
        ratePlanId: serverRateMeta.reservationRatePlanId,
        rateId: serverRateMeta.reservationRoomRateId,
        /* Status flags */
        isTactical: config.LPC_TACTICAL && serverRateMeta.isTactical,
        isAutoLuxPlus: !!serverRateMeta.isAutoLuxplus,
      },
      /* Status flags */
      exceedsIncludedGuests: !!serverRateMeta.exceedsIncludedGuests,
      hasAppliedDiscount: !!serverRateMeta.hasAppliedDiscount,
      isMobilePromotion: false,
      /* Configuration options */
      paymentScheduleInfo: config.PAYMENT_SCHEDULE_HOTEL_ENABLED && serverRateMeta.paymentSchedule
        ? mapPaymentScheduleInfo(serverRateMeta.paymentSchedule)
        : undefined,
      /* Pricing */
      paymentMethod: {
        // For now, LPC rates can only ever be LE collect.
        type: 'leCollect',
      },
      /* Metadata */
      relations: {
        ...baseRelations,
        // We don't expect accommodation service to return self-references, but it doesn't hurt to
        // verify this assumption on our side.
        paymentMethod: {
          leCollect: [],
          propertyCollect: serverRate.relations?.paymentMethod
            ? serverRate.relations.paymentMethod.flatMap(({ rateId, value }) => {
                if (value === 'propertyCollect' && rateId !== serverRate.id) return [rateId]
                return []
              })
            : [],
        },
      },
    }
  } else if (isBedbankMeta(serverRateMeta)) {
    return {
      ...baseRate,
      /* Details */
      service: {
        /* Identifiers */
        type: 'bedbank',
        supplier: serverRateMeta.supplier,
        supplierCode: serverRateMeta.supplierCode!, // This _should_ be always set.
        propertyId: serverRateMeta.bedbankPropertyId,
        roomId: serverRateMeta.bedbankRoomId,
        rateId: serverRateMeta.bedbankRateId,
        token: serverRateMeta.token,
        /* Deduplication keys */
        rateGroupId: serverRateMeta.rateGroupId,
        /* Status flags */
        pricingMethod: serverRateMeta.pricingMethod,
        /* Configuration options */
        bedGroups: serverRateMeta.bedGroups ?? EmptyArray,
      },
      /* Status flags */
      exceedsIncludedGuests: false,
      hasAppliedDiscount: false,
      isMobilePromotion: !!serverRateMeta.isMobilePromotion,
      /* Configuration options */
      paymentScheduleInfo: config.PAYMENT_SCHEDULE_LPP_ENABLED && serverRateMeta.paymentSchedule
        ? mapPaymentScheduleInfo(serverRateMeta.paymentSchedule)
        : undefined,
      /* Pricing */
      paymentMethod: mapPaymentMethod(serverRateMeta, currency),
      /* Metadata */
      relations: {
        ...baseRelations,
        // We don't expect accommodation service to return self-references, but it doesn't hurt to
        // verify this assumption on our side.
        paymentMethod: {
          leCollect: (serverRateMeta.paymentMethod !== 'leCollect' && serverRate.relations?.paymentMethod)
            ? serverRate.relations.paymentMethod.flatMap(({ rateId, value }) => {
                if (value === 'leCollect' && rateId !== serverRate.id) return [rateId]
                return []
              })
            : [],
          propertyCollect: (serverRateMeta.paymentMethod !== 'propertyCollect' && serverRate.relations?.paymentMethod)
            ? serverRate.relations.paymentMethod.flatMap(({ rateId, value }) => {
                if (value === 'propertyCollect' && rateId !== serverRate.id) return [rateId]
                return []
              })
            : [],
        },
      },
    }
  }
  assertUnreachable(serverRateMeta)
}

function mapPropertyRateListItem({
  id,
  upliftReason,
}: ServerRateListItem): App.PropertyRateListItem {
  const [orderReason, calloutReason] = upliftReason
    ? mapPropertyRateUpliftReason(upliftReason)
    : [undefined, undefined]
  return { id, orderReason, calloutReason }
}

function mapPropertyRateUpliftReason(
  upliftReason: NonNullable<ServerRateListItem['upliftReason']>,
): [
  NonNullable<App.EnrichedPropertyRate['orderReason']>,
  NonNullable<App.EnrichedPropertyRate['calloutReason']>,
] {
  switch (upliftReason) {
    case 'highlighted':
      return ['uplift-highlighted', 'highlighted']
    case 'recommended-bookings-occupancy':
      return ['uplift-recommended-bookings-occupancy', 'popular-occupancy']
    default:
      assertUnreachable(upliftReason)
  }
}

function mapPropertyRooms(
  serverRooms: ServerAvailabilityResponseProperty['rooms'],
  currency: string,
  areFlightsEnabled: boolean,
  markupSource?: ChannelMarkupSource,
): Array<App.RawPropertyRoomRates> {
  // NB: Currently, accommodation service gives us _one_ ordering for rooms. Theoretically, the
  // rooms might be ordered differently between hotel-only and hotel-with-flights views. However,
  // this _should_ be rare; we'll cross that bridge when we come to it.
  const sortedServerRooms = sortBy(Object.entries(serverRooms), ([_, { sort }]) => sort, 'asc')
  return sortedServerRooms.map(([roomId, serverRoom]) => {
    const serverUnavailableReason = serverRoom.unavailableReason
    if (serverUnavailableReason) {
      if (serverUnavailableReason === 'FILTERED_OUT') {
        invariant(false, 'Accommodation returned FILTERED_OUT for property room rates')
      }
      return {
        roomId,
        unavailableReason: serverUnavailableReason,
      }
    }
    const rates = mapObject(serverRoom.ratesMap, serverRate => mapPropertyRate(serverRate, currency, serverRoom.ratesMap, markupSource))
    return {
      roomId,
      rates,
      lists: {
        hotelOnly: serverRoom.lists.hotelOnly.map(mapPropertyRateListItem),
        hotelWithFlights: areFlightsEnabled ? serverRoom.lists.hotelWithFlights.map(mapPropertyRateListItem) : [],
      },
    }
  })
}

export default function mapPropertyRates(
  serverProperty: ServerAvailabilityResponseProperty,
  currency: string,
  areFlightsEnabled: boolean,
  markupSource?: ChannelMarkupSource,
): Record<string, Array<App.RawPropertyRoomRates>> {
  const result: Record<string, Array<App.RawPropertyRoomRates>> = {
    combined: mapPropertyRooms(serverProperty.rooms, currency, areFlightsEnabled, markupSource),
  }
  for (const [occupancyKey, occupancy] of Object.entries(serverProperty.occupancies ?? {})) {
    result[occupancyKey] = mapPropertyRooms(occupancy.rooms, currency, areFlightsEnabled, markupSource)
  }
  return result
}
